import { Link } from "react-router-dom";
import { useSite, usePageMeta } from "@/hooks/useSite";
import { orgJsonLd } from "@/lib/seo";
import { SiteLayout } from "@/components/SiteLayout";
import { Picture } from "@/components/Picture";
import { TimelineEntry } from "@/components/TimelineEntry";

const LEARNED = [
  ["Small and fast works", "Our most useful work cost little and came together in weeks."],
  ["Food brings people to the table", "Dialogues over shared local food worked better than any conference hall."],
  ["Networks hold when chains break", "In each crisis, it was the people we had met and mapped who responded."],
  ["A big event needs a strong network first", "That is the order we are following now."],
];
const PARTNERS = ["Social Gastronomy Movement", "Food Networks", "KHETI", "Almost Heaven Farms", "Development Voyage", "rundumkultur", "THE BAZAAR", "Li-BIRD", "IAAS Nepal", "Raithaane", "Sano Paila", "Khali Khutta", "Firfirey Organics", "thegundruk.com", "NYEF Pokhara", "BlinkNow / Kopila Valley School", "Vintuna Youth Club", "Ministry of Agriculture, Province 1 (now Koshi)", "Ministry of Land Management, Agriculture and Cooperatives, Gandaki Province", "Pokhara Metropolitan City"];
const SUPPORTERS = ["Foundation Temperatio", "Däster Schild Stiftung", "Cuisine sans frontières and individual donors in Switzerland", "Social Gastronomy Movement", "NYEF Pokhara", "Li-BIRD"];

const Names = ({ list }) => <p className="names">{list.map((n) => <span key={n}>{n}</span>)}</p>;

const Beginning = () => (
  <section className="section">
    <div className="wrap two-col prose">
      <div>
        <h2>The beginning</h2>
      </div>
      <div>
        <p>Nepal Food started in 2019 as an idea and a team effort: a national fair and summit to celebrate the diversity of Nepal's food, and to open a wide conversation about its future with the people who grow it, cook it, sell it and eat it.</p>
        <p>The first Nepal Food Fair &amp; Summit was planned for December 2020 in the Kathmandu Valley. In July 2020 we registered Nepal Food Networks as a not-for-profit company to hold that space.</p>
        <p className="pivot">Then COVID-19 arrived.</p>
        <p>We postponed the fair. Many of us worked in agriculture, and we saw what the lockdowns did up close: farmers with harvests and no market, young people without work, families without enough to eat. So we turned to what was needed, and kept the larger conversation going in smaller ways.</p>
      </div>
    </div>
  </section>
);

const Learned = () => (
  <section className="section dark net-pattern" aria-labelledby="learned-title">
    <div className="wrap">
      <h2 id="learned-title">What we learned</h2>
      <div className="learned">
        {LEARNED.map(([h, p]) => <div key={h}><h3>{h}</h3><p>{p}</p></div>)}
      </div>
    </div>
  </section>
);

const Thanks = () => (
  <section className="section" aria-labelledby="thanks-title">
    <div className="wrap">
      <h2 id="thanks-title">Thank you</h2>
      <p>None of this was done alone. We thank everyone who served on our team and advisory board, the students, volunteers, farmers and cooks who gave their time, and the partners and supporters who stood with us.</p>
      <h3 style={{ marginTop: "2rem" }}>Partners, 2019–2022</h3>
      <Names list={PARTNERS} />
      <h3 style={{ marginTop: "1.5rem" }}>Supporters, 2020–2021</h3>
      <Names list={SUPPORTERS} />
    </div>
  </section>
);

const Closing = ({ summitOn }) => (
  <section className="section closing" aria-labelledby="now-title">
    <div className="wrap">
      <div>
        <h2 id="now-title">And now</h2>
        <p className="lede" style={{ color: "var(--ink)" }}>This November we co-host the SGM Nepal Summit 2026 in Kathmandu and Pokhara. It is our first step back, with new people and the same idea we started with.</p>
        <div className="actions">
          <Link className="btn" to={summitOn ? "/#summit" : "/#join"} data-testid="story-about-summit-button">About the summit</Link>
          <Link className="btn ghost" to="/#join" data-testid="story-join-button">Join the network</Link>
        </div>
      </div>
      <figure style={{ margin: 0 }}>
        <Picture src="/assets/img/fishing-nets.jpg" alt="Four women walk along a riverbank carrying large round fishing nets." width={1400} height={991} lazy />
        <figcaption className="credit" style={{ marginTop: ".5rem" }}>Photo: Nabin Baral and Gagan Thapa</figcaption>
      </figure>
    </div>
  </section>
);

export default function OurStory() {
  const { data } = useSite();
  usePageMeta({
    title: "Our story – Nepal Food Networks",
    description: "How Nepal Food Networks began in 2019, and what we did when the world stopped: Solidarity Farmer, flood recovery, food systems dialogues across Nepal.",
    path: "/our-story",
    jsonLd: data ? [orgJsonLd(data.settings)] : [],
    verification: data?.settings?.verification,
  });
  if (!data) return null;
  return (
    <SiteLayout data={data} current="story">
      <section className="story-hero">
        <div className="wrap">
          <div>
            <h1>Our story</h1>
            <p className="lede">How Nepal Food Networks began, and what we did when the world stopped.</p>
          </div>
          <Picture src="/assets/img/grain-pounding.jpg" alt="Three women pound grain with tall wooden pestles on a hillside threshing ground." width={797} height={1126} fetchPriority="high" />
        </div>
      </section>
      <Beginning />
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <h2>What we did, 2019 to 2022</h2>
          <ol className="timeline" data-testid="timeline-list">
            {(data.timeline || []).map((e) => <TimelineEntry key={e.id} entry={e} />)}
          </ol>
        </div>
      </section>
      <Learned />
      <Thanks />
      <Closing summitOn={!!data.summit?.published} />
    </SiteLayout>
  );
}
